// WelcomeModal.js - First-time user welcome screen

import React from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';

const WelcomeModal = ({ onStart, onSkip }) => {
  return (
    <Overlay>
      <Modal>
        <Header>
          <Title>Welcome to GChain!</Title>
          <Subtitle>RF Link Budget & Signal Chain Modeling</Subtitle>
        </Header>

        <Content>
          <Section>
            <SectionTitle>What is GChain?</SectionTitle>
            <Description>
              GChain is a powerful visual tool for designing and analyzing RF signal chains.
              Drag and drop components, connect them together, and see real-time
              calculations for gain, noise figure, compression, and more.
            </Description>
          </Section>

          <Section>
            <SectionTitle>Key Features</SectionTitle>
            <FeatureList>
              <Feature>
                <FeatureIcon>📡</FeatureIcon>
                <FeatureText>
                  <strong>20+ RF Components</strong> - Sources, amplifiers, filters, mixers, and more
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>📊</FeatureIcon>
                <FeatureText>
                  <strong>Frequency Sweep Analysis</strong> - Single frequency or full sweep with plots
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>🔍</FeatureIcon>
                <FeatureText>
                  <strong>Guerrilla RF Catalog</strong> - Real parts with datasheet-based frequency data
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>💾</FeatureIcon>
                <FeatureText>
                  <strong>Save & Export</strong> - PDF reports with BOM, Excel exports, project management
                </FeatureText>
              </Feature>
            </FeatureList>
          </Section>

          <Section>
            <SectionTitle>Ready to get started?</SectionTitle>
            <Description>
              Take a quick 27-step interactive tour to learn the basics, or jump right in and explore on your own.
              You can pause the tutorial anytime and resume later.
            </Description>
          </Section>
        </Content>

        <Footer>
          <SecondaryButton onClick={onSkip}>
            Skip Tutorial
          </SecondaryButton>
          <PrimaryButton onClick={onStart}>
            Start Tutorial
          </PrimaryButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: ${THEME.panelBackground};
  border: 2px solid ${THEME.accentPrimary};
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.3);
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${THEME.accentPrimary} 0%, #8B0000 100%);
  padding: 30px;
  text-align: center;
  border-radius: 10px 10px 0 0;
`;

const Title = styled.h1`
  color: white;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
  font-weight: 300;
`;

const Content = styled.div`
  padding: 30px;
`;

const Section = styled.div`
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: ${THEME.accentPrimary};
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const Description = styled.p`
  color: ${THEME.textPrimary};
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FeatureIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled.div`
  color: ${THEME.textPrimary};
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: ${THEME.accentPrimary};
    font-weight: 600;
  }
`;

const Footer = styled.div`
  padding: 20px 30px 30px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PrimaryButton = styled(Button)`
  background: ${THEME.accentPrimary};
  color: white;

  &:hover {
    background: #FF1744;
    box-shadow: 0 4px 12px rgba(220, 20, 60, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${THEME.textSecondary};
  border: 1px solid #404040;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

export default WelcomeModal;
